import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import type {
  InputDefinition,
  ProvenanceRecord,
  ProvenanceSource,
  ValidRange,
} from '@arcanum-hq/shared-types';
import { Button } from '../ui/Button';
import {
  isAddressedLocation,
  type ProvenanceEntry,
} from '@/lib/version-completeness';
import { useApplyValue } from '@/hooks/use-apply-value';
// AcceptUnderwritingButton + VersionsTriggerButton moved to the consolidated
// <ManageVersionsButton> on the Builder tab row (conversation-model-versioning
// Phase 5). This panel keeps only the <AcceptedUnderwritingBadge> +
// <CurrentVersionIndicator> STATUS indicators (ADR-0019 placement preserved).
import { AcceptedUnderwritingBadge } from './AcceptedUnderwritingBadge';
import { CounterQuad } from './CounterQuad';
import { CurrentVersionIndicator } from './CurrentVersionIndicator';
import { DerivativeBreadcrumb } from './DerivativeBreadcrumb';
import { MarketDataApplyPanel } from './MarketDataApplyPanel';
import {
  SkippedMarketDataBadge,
  type SkippedMarketDataEntry,
} from './SkippedMarketDataBadge';
import { VariableRow } from './VariableRow';

/**
 * Market-data categories the picker queries from the cache.
 * Matches the category strings the apply_market_defaults tool fetches.
 * Start with the highest-value unlock; expand during Phase-5 walkthrough.
 * ADR 0006 D-D.
 * TODO(phase-5-walk): confirm full category set against the apply tool's fetch chain.
 */
const MARKET_DATA_PICKER_CATEGORIES = [
  'market_rents',
  'cap_rates',
  'construction_costs',
  'financing_terms',
  'operating_expenses',
  'land_costs',
] as const;

interface OrphanedInput {
  value: unknown;
  source: string;
  originalSchemaVersion: string;
}

interface AgentState {
  inputValues?: Record<string, unknown>;
  inputProvenance?: Record<string, string | ProvenanceRecord>;
  orphanedInputs?: Record<string, OrphanedInput>;
}

interface VariablesPanelProps {
  agentState: Record<string, unknown> | null;
  /**
   * admin-deal-oversight ADR 0007: when true, every bucket renders read-only
   * (no per-row cell-edit affordance) — used by the admin read-only Builder.
   */
  readOnly?: boolean;
  /**
   * Active conversation id, threaded through to the inline Accept button.
   * Null/undefined when the Builder is on the home view (no active
   * conversation) — the button renders disabled in that case.
   */
  conversationId?: string | null;
  /**
   * Input definitions from the active model schema. Used to look up
   * `validRange` per row for the out-of-range warning. Optional — the
   * panel renders without range checks when absent.
   */
  inputDefinitions?: (Pick<
    InputDefinition,
    'name' | 'displayName' | 'validRange' | 'defaultValue'
  > & {
    /** Used by <ManageVersionsButton> for the location-input check (P5 S1). */
    dataType?: InputDefinition['dataType'];
    /**
     * A1 cell address. Used together with `sheet` to dedup alias-pair schema
     * entries during cold-state synthesis — when two inputs target the same
     * `(sheet, cell)` tuple and one is already in `inputProvenance`, the
     * alias is suppressed instead of rendered as a separate ARCANUM row.
     * (e.g. `ConstrPeriod` on DealSummary!C22 is hidden when its alias
     * `ConstructPeriod` on DealSummary!C22 is user-set.)
     *
     * The dedup key MUST include the sheet (not just the bare cell address):
     * the schema's `cell` field is sheet-unqualified, so two unrelated
     * inputs on different sheets can carry the same `cell` value
     * (e.g. multifamily-v1's `LoanToCost` on DealSummary!F14 vs
     * `ConstructContingencyPct` on DevBudget!F14). Without the sheet
     * qualifier, setting one would falsely suppress the other from the
     * cold-state ARCANUM bucket.
     */
    cell?: InputDefinition['cell'];
    /** Sheet name the input lives on. Pairs with `cell` for the dedup key. */
    sheet?: InputDefinition['sheet'];
  })[];
  /**
   * Table-input definitions from the active model schema. Two consumers:
   *
   * (1) The CounterQuad's EDITABLE total — each table contributes
   *     `rows.length × columns.length` cells. (new-development-input-
   *     roundtrip 2026-05-26 — aligns the panel denominator with the
   *     MODEL OVERVIEW `totalInputs` formula in builder.tsx:1252-1258.)
   *
   * (2) Cold-state default synthesis (this feature) — iterates rows.cells
   *     × columns to surface each table cell's default value as an ARCANUM
   *     bucket row when no user-set entry exists. Each row carries
   *     `defaultValues: Record<columnKey, value>` so the synthetic row
   *     can show the resolved default; each column carries `displayName`
   *     for the row label (`${row.label} – ${column.displayName}`).
   */
  tableInputs?: Array<{
    name?: string;
    rows: Array<{
      index?: number;
      label?: string;
      cells: Record<string, string>;
      defaultValues?: Record<string, unknown>;
      active?: boolean;
    }>;
    columns: Array<{
      key: string;
      displayName?: string;
      dataType?: string;
      displayFormat?: string;
    }>;
  }>;
  /**
   * Latest `apply_market_defaults.skipped[]` payload from the most recent
   * tool call this turn. Drives `<SkippedMarketDataBadge>` in the header.
   * (Phase 3 Session 4)
   */
  skippedMarketData?: SkippedMarketDataEntry[];
  /**
   * Apply-result transparency accounting from `apply_market_defaults` Phase 3.
   * Extracted from the apply-result in the `latestToolPayloads` memo on
   * `builder.tsx` and threaded here to the badge mount.
   * Shape mirrors `applyMarketDefaultsResultSchema.fetchedVsApplied` verbatim
   * (fetched / applied / noTarget). Optional — pre-Phase-3 results carry none.
   * (market-defaults-application Phase 3 Session 3.4)
   */
  fetchedVsApplied?: {
    fetched: number;
    applied: number;
    noTarget: number;
  };
  /**
   * Itemized list of fields successfully applied by the latest
   * `apply_market_defaults` call. Threaded to both `<SkippedMarketDataBadge>`
   * (expandable "M applied" segment) and `<MarketDataApplyPanel>` (filter out
   * already-applied fields from the picker). Default [].
   * (market-defaults-application WIN 1 / WIN 2, 2026-06-10)
   */
  appliedMarketFields?: Array<{
    inputName: string;
    marketField: string;
    source: string;
  }>;
  /**
   * market-default-apply-ux Phase 2 Session 2.7 — exact ADR-0008 partition
   * counts for the `<MarketDataApplyPanel>` teaser, computed in builder.tsx
   * from the same apply_market_defaults tool_result `<MarketDefaultApplyReceipt>`
   * reads (applied = location-excluded applied[]+appliedTableCells[]; pulled =
   * informationalSet+genuinelyUnmatchedSet). When absent the panel falls back to
   * its cache-derived approximation.
   */
  marketApplyCounts?: { applied: number; pulled: number };
  /**
   * The id of the version pointed to by `current_version_id` on the active
   * conversation. Passed down from the builder route (which resolves it from
   * the Session-2-widened `ConversationItem` list — the detail endpoint does
   * NOT expose this pointer). Drives `<CurrentVersionIndicator>`.
   * (conversation-model-versioning Phase 5 Session 4, ADR 0019)
   */
  currentVersionId?: string | null;
  /**
   * The id of the version pointed to by `accepted_underwriting_version_id`.
   * Passed down from the builder route (same source as `currentVersionId`).
   * Drives `<AcceptedUnderwritingBadge>`.
   * (conversation-model-versioning Phase 5 Session 4, ADR 0019)
   */
  acceptedUnderwritingVersionId?: string | null;
}

type Bucket = 'USER' | 'ARCANUM' | 'DERIVED';

function bucketForSource(source: ProvenanceSource | undefined): Bucket {
  if (source === 'user') return 'USER';
  if (source === 'calculated' || source === 'scenario') return 'DERIVED';
  // 'market_data' | 'default' | 'inferred' | undefined
  return 'ARCANUM';
}

function getRecord(
  prov: string | ProvenanceRecord | undefined
): ProvenanceRecord | null {
  if (!prov || typeof prov === 'string') return null;
  return prov;
}

function getSource(
  prov: string | ProvenanceRecord | undefined
): ProvenanceSource | undefined {
  if (!prov) return undefined;
  if (typeof prov === 'string') {
    // Legacy string-only provenance — narrow if it matches the enum, else
    // fall through to undefined (bucket lands as ARCANUM, the catch-all).
    const validSources: ProvenanceSource[] = [
      'user',
      'default',
      'calculated',
      'scenario',
      'market_data',
      'inferred',
    ];
    return (validSources as string[]).includes(prov)
      ? (prov as ProvenanceSource)
      : undefined;
  }
  return prov.source;
}

/**
 * Same display formatter as the legacy panel — kept inline to preserve the
 * Excel-serial-date heuristic + percent / int / large-number paths.
 */
function formatValue(val: unknown, key?: string): string {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'number') {
    if (
      val > 40000 &&
      val < 55000 &&
      (key?.toLowerCase().includes('date') ||
        key?.toLowerCase().includes('start'))
    ) {
      const base = new Date(1899, 11, 30);
      const d = new Date(base.getTime() + val * 86400000);
      return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    }
    if (val > 0 && val < 1) return `${(val * 100).toFixed(2)}%`;
    if (val >= 1000) return val.toLocaleString();
    return String(val);
  }
  if (typeof val === 'string') return val;
  return JSON.stringify(val);
}

interface RowData {
  name: string;
  displayName?: string;
  value: unknown;
  formattedValue: string;
  provenance: ProvenanceRecord | null;
  validRange?: ValidRange;
}

interface BucketSectionProps {
  bucket: Bucket;
  rows: RowData[];
  /** Controlled expanded state — lifted to the panel so the deep-link
   *  listener can force-expand a bucket when scroll-to-input arrives. */
  expanded: boolean;
  onToggle: () => void;
  /** Input-definition lookup map used to resolve `derivedFrom` raw keys to
   *  human-readable displayNames in `<DerivativeBreadcrumb>`. */
  inputDefMap: Map<string, { displayName?: string }>;
  /**
   * Optional sub-counter decomposition rendered inline next to the bucket
   * count. Used by ARCANUM to surface "X confirmed · Y unreviewed" so the
   * user can see at a glance that an accept_defaults call moved entries
   * from unreviewed → confirmed (the source bucket sort is by `source`,
   * not `defaultStatus`, so the parent count stays unchanged on accept).
   * variables-counter-coherence Inc-5 (2026-05-20).
   */
  subCounts?: { confirmed: number; unreviewed: number };
  /**
   * When true, every row in this section is rendered read-only (no status /
   * confidence / out-of-range chrome; label in `text-purple`). Used by the
   * DERIVED bucket whose rows are computed geocode values, not user inputs.
   * builder-ux-overhaul WS2 Session 4, ADR 0005.
   */
  readOnly?: boolean;
}

interface ScrollToInputListenerProps {
  buckets: Record<Bucket, RowData[]>;
  bucketExpanded: Record<Bucket, boolean>;
  setBucketExpanded: React.Dispatch<
    React.SetStateAction<Record<Bucket, boolean>>
  >;
  panelRef: React.RefObject<HTMLDivElement | null>;
}

/**
 * Listens for `arcanum:scroll-to-input` window events (dispatched by the
 * route's `?input=` deep-link handler). On match:
 *   1. Identify which bucket contains the named-range row.
 *   2. Force-expand the bucket if collapsed.
 *   3. After the next paint, scroll the row into view + flash highlight
 *      via `data-flash-state="on"` (CSS-driven 800ms animation).
 *
 * No-op when the named range isn't present in any bucket — graceful
 * degradation per Session 5 contract (silent failure, console.warn only).
 */
function ScrollToInputListener({
  buckets,
  bucketExpanded,
  setBucketExpanded,
  panelRef,
}: ScrollToInputListenerProps) {
  // Track the latest values via refs so the single-mount window listener
  // reads fresh state on every dispatch without re-binding.
  const bucketsRef = useRef(buckets);
  const expandedRef = useRef(bucketExpanded);
  // Pending ref handles the cold-load hydration race: a `?input=X` deep link
  // dispatches `arcanum:scroll-to-input` before conversation data loads, so
  // bucketsRef is empty at handler time. We stash the namedRange here and
  // retry on the next bucket update (sibling effect below).
  const pendingRef = useRef<string | null>(null);
  useEffect(() => {
    bucketsRef.current = buckets;
  }, [buckets]);
  useEffect(() => {
    expandedRef.current = bucketExpanded;
  }, [bucketExpanded]);

  const consume = useCallback(
    (namedRange: string) => {
      const ownerBucket = (Object.keys(bucketsRef.current) as Bucket[]).find(
        (key) => bucketsRef.current[key].some((r) => r.name === namedRange)
      );
      if (!ownerBucket) {
        // Differentiate "buckets not loaded yet" (retry on next update) from
        // "buckets loaded but namedRange genuinely unknown" (give up + warn).
        const totalRows = (Object.keys(bucketsRef.current) as Bucket[]).reduce(
          (n, k) => n + bucketsRef.current[k].length,
          0
        );
        if (totalRows === 0) {
          pendingRef.current = namedRange;
        } else {
          pendingRef.current = null;

          console.warn('[deep-link-resolution]', {
            surface: 'variables',
            reason: 'unknown_named_range',
            param: namedRange,
            deep_link_resolution_failed: true,
          });
        }
        return;
      }
      pendingRef.current = null;
      if (!expandedRef.current[ownerBucket]) {
        setBucketExpanded((prev) => ({ ...prev, [ownerBucket]: true }));
      }
      // Wait for the bucket-expand re-render to commit before scrolling.
      // Two RAFs is the canonical post-paint hook in JSDOM-friendly code.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const root = panelRef.current ?? document;
          const row = root.querySelector(
            `[data-testid="variable-row"][data-name="${CSS.escape(
              namedRange
            )}"]`
          );
          if (!row) return;
          if (
            typeof (row as Element & { scrollIntoView?: unknown })
              .scrollIntoView === 'function'
          ) {
            (row as HTMLElement).scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
          row.setAttribute('data-flash-state', 'on');
          window.setTimeout(() => row.removeAttribute('data-flash-state'), 800);
        });
      });
    },
    [panelRef, setBucketExpanded]
  );

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ namedRange?: unknown }>).detail;
      const namedRange =
        typeof detail?.namedRange === 'string' ? detail.namedRange : null;
      if (!namedRange) return;
      consume(namedRange);
    };
    window.addEventListener('arcanum:scroll-to-input', handler);
    return () => window.removeEventListener('arcanum:scroll-to-input', handler);
  }, [consume]);

  // Retry pending deep-link when buckets populate. Solves the cold-load race
  // where `arcanum:scroll-to-input` fires before conversation data loads.
  useEffect(() => {
    if (!pendingRef.current) return;
    const totalRows = (Object.keys(buckets) as Bucket[]).reduce(
      (n, k) => n + buckets[k].length,
      0
    );
    if (totalRows > 0) consume(pendingRef.current);
  }, [buckets, consume]);

  return null;
}

function BucketSection({
  bucket,
  rows,
  expanded,
  onToggle,
  inputDefMap,
  subCounts,
  readOnly = false,
}: BucketSectionProps) {
  // Empty-state: return null so an empty DERIVED bucket (model with no geocode
  // data yet) doesn't render an awkward empty section header. This is the
  // correct 4-state "empty" handling for the now-meaningful DERIVED bucket
  // (builder-ux-overhaul WS2 Session 4, ADR 0005).
  if (rows.length === 0) return null;

  const accentClass: Record<Bucket, string> = {
    USER: 'text-accent',
    ARCANUM: 'text-warn',
    DERIVED: 'text-purple',
  };

  return (
    <>
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-2.5 border-b border-panel-border flex items-center justify-between bg-transparent border-none cursor-pointer hover:bg-panel-bg/40 transition-colors"
        data-testid={`section-${bucket.toLowerCase()}-toggle`}
        data-expanded={expanded ? 'true' : 'false'}
      >
        <span
          className={clsx(
            'text-xs font-mono font-semibold tracking-[0.3px] flex items-baseline gap-2',
            accentClass[bucket]
          )}
        >
          <span>
            {bucket} ({rows.length})
          </span>
          {subCounts && (
            <span
              className="text-[9.5px] font-normal text-text-dim tracking-[0.2px]"
              data-testid={`section-${bucket.toLowerCase()}-subcounts`}
            >
              {subCounts.confirmed} confirmed · {subCounts.unreviewed}{' '}
              unreviewed
            </span>
          )}
        </span>
        <span className="text-[10px] text-text-dim font-mono">
          {expanded ? '▲' : '▼'}
        </span>
      </button>
      {expanded &&
        rows.map((row) => {
          const derivedFrom = row.provenance?.derivedFrom;
          const sourceDetail = row.provenance?.sourceDetail;
          // Parent-source label: prefer the parent's `sourceDetail` (e.g.
          // "FRED MORTGAGE30US") prepended to the parent display name.
          // For aggregate-distributed table cells (source:'user',
          // derivedFrom:'total_units', no sourceDetail), look up the
          // parent's displayName from inputDefMap so the breadcrumb reads
          // "Derived from Total Units" rather than the raw key "total_units".
          // Falls back to the bare derivedFrom key when no displayName exists.
          const derivedFromDisplay = derivedFrom
            ? inputDefMap.get(derivedFrom)?.displayName ?? derivedFrom
            : null;
          const parentSource = derivedFrom
            ? sourceDetail
              ? `${sourceDetail} → ${derivedFromDisplay}`
              : derivedFromDisplay
            : null;
          return (
            <div key={row.name}>
              <VariableRow
                name={row.name}
                displayName={row.displayName}
                value={row.value}
                formattedValue={row.formattedValue}
                provenance={row.provenance}
                validRange={row.validRange}
                readOnly={readOnly}
              />
              {parentSource && (
                <div className="px-4 pb-2 -mt-1 border-b border-panel-border">
                  <DerivativeBreadcrumb
                    child={row.name}
                    parentSource={parentSource}
                  />
                </div>
              )}
            </div>
          );
        })}
    </>
  );
}

export function VariablesPanel({
  agentState,
  readOnly = false,
  conversationId = null,
  inputDefinitions,
  tableInputs,
  skippedMarketData,
  fetchedVsApplied,
  appliedMarketFields,
  marketApplyCounts,
  currentVersionId = null,
  acceptedUnderwritingVersionId = null,
}: VariablesPanelProps) {
  const [showOrphaned, setShowOrphaned] = useState(false);
  // Phase 3 Session 5: lifted bucket-expanded state. USER opens by default;
  // ARCANUM and DERIVED start collapsed. The deep-link listener writes
  // into this map directly to force-expand a bucket containing the
  // requested input.
  const [bucketExpanded, setBucketExpanded] = useState<Record<Bucket, boolean>>(
    {
      USER: true,
      ARCANUM: false,
      DERIVED: false,
    }
  );
  const toggleBucket = (bucket: Bucket) =>
    setBucketExpanded((prev) => ({ ...prev, [bucket]: !prev[bucket] }));
  const panelRef = useRef<HTMLDivElement | null>(null);

  // ── market-defaults-application Phase 4 Session 4.5 ──────────────────────
  // Read the metro from dealMetadata. The full cast is resolved below once we
  // have `agentState` as AgentState, but we need the metro HERE — above any
  // early return — for Rules of Hooks compliance (ADR 0006 / root CLAUDE.md).
  // The cast mirrors the one at VariablesPanel.tsx:515-519 (now renumbered).
  const _earlyDealMetadata =
    (agentState?.dealMetadata as
      | ({ market?: string | null } & Record<string, unknown>)
      | null
      | undefined) ?? null;
  const _metro = _earlyDealMetadata?.market ?? null;

  // UNCONDITIONAL hook call — ABOVE any early return (Rules of Hooks).
  // Self-gates via `enabled` when metro is null or readOnly.
  // apply.rows is still consumed by <MarketDataApplyPanel> (teaser "also pulled"
  // count). apply.applyValue / apply.isApplying are no longer passed to the
  // panel (the picker was removed by market-default-apply-ux Phase 2 Session 2.7).
  const apply = useApplyValue({
    conversationId: conversationId ?? '',
    metro: _metro,
    categories: MARKET_DATA_PICKER_CATEGORIES as unknown as string[],
    enabled: !readOnly && !!conversationId && !!_metro,
  });
  // ── end Phase 4 Session 4.5 hook block ───────────────────────────────────

  const state = agentState as AgentState | null;
  const values = state?.inputValues ?? {};
  const provenance = state?.inputProvenance ?? {};
  const orphanedInputs = state?.orphanedInputs ?? {};
  // dealMetadata is used by <ManageVersionsButton> for the location-input
  // resolution check (ADR 0003 / P5 Session 1), and by the location row
  // affordance below to read the resolved canonical address per input key.
  // Cast via the raw agentState since AgentState interface above only covers
  // input-tracking fields.
  const dealMetadata =
    (agentState?.dealMetadata as
      | ({ market?: string | null } & Record<string, unknown>)
      | null
      | undefined) ?? null;
  const orphanedEntries = Object.entries(orphanedInputs);
  const orphanedCount = orphanedEntries.length;
  const entries = Object.entries(values);

  const inputDefMap = new Map(inputDefinitions?.map((d) => [d.name, d]) ?? []);

  // Collect location inputs from the schema — these are rendered in a
  // dedicated section (ADR 0023: display + affordance only, no inline editor).
  const locationInputDefs =
    inputDefinitions?.filter((d) => d.dataType === 'location') ?? [];

  // Build a Set of location input names so the bucket loop below can skip
  // them — avoids double-rendering a location input that is already set
  // (and therefore present in inputValues).
  const locationInputNames = new Set(locationInputDefs.map((d) => d.name));

  const buckets: Record<Bucket, RowData[]> = {
    USER: [],
    ARCANUM: [],
    DERIVED: [],
  };

  // variables-counter-coherence Inc-5 (2026-05-20): sub-decompose the ARCANUM
  // bucket into "confirmed" (defaultStatus ∈ {confirmed, accepted, overridden})
  // and "unreviewed" (null/undefined) so the bucket header shows
  // `ARCANUM (57) [40 confirmed · 17 unreviewed]`. Pre-fix the bucket count
  // stayed at 57 whether the user had run accept_defaults or not — the source
  // bucket sort is by `source`, not `defaultStatus`, so accept_defaults
  // (provenance-only flip) was visually invisible to the user.
  let arcanumConfirmed = 0;
  let arcanumUnreviewed = 0;

  for (const [name, value] of entries) {
    // Location inputs are rendered in their own section; skip the 3-tier
    // bucket display for them so they don't appear twice.
    if (locationInputNames.has(name)) continue;
    const record = getRecord(provenance[name]);
    const source = getSource(provenance[name]);
    // Counter-denominator coherence (new-development-input-roundtrip
    // 2026-05-26): Location geocoder sub-attributes (Location.city,
    // Location.state, Location.zip, Location.coordinates, .floodZone, etc.)
    // are now surfaced inline UNDER the Location section's resolved row
    // (see <location-resolved-details> below). Skip them from the DERIVED
    // bucket so they don't render twice. Filter by either derivedFrom
    // matching a Location-input name (canonical geocoder write) OR the
    // namespaced-key fallback (`Location.…`) for defense-in-depth against
    // writeback paths that omit `derivedFrom`. Forward-compatible: non-
    // Location derived rows (none today) still land in DERIVED.
    if (
      (record?.derivedFrom != null &&
        locationInputNames.has(record.derivedFrom)) ||
      Array.from(locationInputNames).some((locName) =>
        name.startsWith(`${locName}.`)
      )
    ) {
      continue;
    }
    // builder-ux-overhaul WS2 discriminator (ADR 0005, 2026-05-22):
    //   1. Has a schema InputDefinition → EDITABLE (USER if source:'user', else ARCANUM)
    //   2. source:'user' + no InputDefinition → USER (table count cells distributed
    //      by set_table_inputs carry source:'user' + derivedFrom:'<aggregate>' but
    //      are NOT in the schema; the user explicitly set them — keep in USER bucket)
    //   3. derivedFrom set AND no InputDefinition AND source != 'user' → DERIVED
    //      (geocode Location.* sub-attrs: source:'market_data', derivedFrom:'Location')
    //   4. Fallthrough → source-based (preserves existing behaviour for entries
    //      that are neither InputDef-backed nor derivedFrom-tagged)
    const hasInputDef = inputDefMap.has(name); // PRESENCE TEST — NOT a recount
    const effectiveSource = source ?? record?.source;
    // builder-ux-overhaul FEATURE_END (2026-05-22): a non-user, non-schema entry
    // with a namespaced key (`Location.coordinates`, `Location.censusTract`, …)
    // is a derived geocode sub-attr → DERIVED, even when the writeback omitted
    // `derivedFrom` (defense-in-depth for the server fix that now stamps it).
    // Table-count cells (`Studio_Units`, set_table_inputs composite keys) are
    // source:'user' and are handled by the `=== 'user'` arm above, so they never
    // reach this branch. ADR 0005.
    const isDerivedGeocodeSubAttr =
      effectiveSource !== 'user' &&
      (record?.derivedFrom != null || name.includes('.'));
    const bucket: Bucket = hasInputDef
      ? effectiveSource === 'user'
        ? 'USER'
        : 'ARCANUM'
      : effectiveSource === 'user'
      ? 'USER'
      : isDerivedGeocodeSubAttr
      ? 'DERIVED'
      : bucketForSource(effectiveSource);
    const def = inputDefMap.get(name);
    buckets[bucket].push({
      name,
      displayName: def?.displayName,
      value,
      formattedValue: formatValue(value, name),
      provenance: record,
      validRange: def?.validRange,
    });
    if (bucket === 'ARCANUM') {
      const ds = record?.defaultStatus;
      if (ds === 'confirmed' || ds === 'accepted' || ds === 'overridden') {
        arcanumConfirmed++;
      } else {
        arcanumUnreviewed++;
      }
    }
  }

  // Cold-state default-row synthesis (new-development-input-roundtrip
  // 2026-05-26): the agent's `initializeDefaults` populates the in-memory
  // tracker with default entries but those don't reach `inputProvenance`
  // until a state-mutating tool fires `recordEdit`. On conversation load
  // the panel would render 0 bucket rows (just the Location section)
  // until the user touched something. To make the 67 defaults visible
  // upfront (42 scalars + 25 table cells for multifamily-v1), iterate
  // every schema slot and synthesize an ARCANUM row for any that's
  // missing from `entries`. Each synthetic row carries `source: 'default'`,
  // `defaultStatus: null`, and the schema's `defaultValue` so the user
  // sees the actual starting point.
  //
  // Gating: only fires when an input/cell has a `defaultValue` populated
  // (preserves back-compat for tests that omit it). Location inputs are
  // explicitly skipped — they're rendered in the dedicated section, not
  // bucketed.
  //
  // Cross-surface coherence: this does NOT inflate the MODEL OVERVIEW
  // `userInputCount` numerator (that reads `inputProvenance` directly and
  // counts only user-addressed entries). The synthesized rows are
  // display-only — they exist in the bucket map for rendering, never
  // touch the DB. The user sees "0 YOUR · 67 ARCANUM · 0 DERIVED · 68
  // EDITABLE" on cold load, which matches the user's mental model.
  const seenNames = new Set(entries.map(([n]) => n));

  // Alias-pair dedup (new-development-input-roundtrip 2026-05-26): build a
  // Set of `${sheet}!${cell}` keys already covered by any provenance entry
  // that maps to a schema input. Two schema inputs can target the same
  // Excel cell on the same sheet (e.g. `ConstructPeriod` + `ConstrPeriod`
  // legacy alias on DealSummary!C22). When the agent writes one, the other
  // stays absent from `inputProvenance` and would otherwise be synthesized
  // as a stale ARCANUM row even though the cell IS set.
  //
  // Sheet qualifier is REQUIRED in the key (root CLAUDE.md backlog cite:
  // 2026-05-26 follow-up to the new-development-input-roundtrip walkthrough):
  // the schema's `cell` field is sheet-unqualified, so two unrelated inputs
  // on different sheets can share the same bare `cell` value
  // (multifamily-v1's `LoanToCost` on DealSummary!F14 vs
  // `ConstructContingencyPct` on DevBudget!F14). Without the sheet
  // qualifier, setting one would falsely suppress the other from
  // cold-state synthesis.
  const cellKey = (sheet: string | undefined, cell: string | undefined) =>
    sheet && cell ? `${sheet}!${cell}` : null;
  const nameToCellKey = new Map<string, string>();
  for (const def of inputDefinitions ?? []) {
    const key = cellKey(def.sheet, def.cell);
    if (key) nameToCellKey.set(def.name, key);
  }
  const coveredCellKeys = new Set<string>();
  for (const [name] of entries) {
    const key = nameToCellKey.get(name);
    if (key) coveredCellKeys.add(key);
  }

  // Pass 1 — scalar inputs missing from entries.
  for (const def of inputDefinitions ?? []) {
    if (def.dataType === 'location') continue;
    if (locationInputNames.has(def.name)) continue;
    if (seenNames.has(def.name)) continue;
    // Alias-pair guard — a sibling input sharing this exact (sheet, cell)
    // is already covered, so the cell IS set even if this name isn't.
    const defCellKey = cellKey(def.sheet, def.cell);
    if (defCellKey && coveredCellKeys.has(defCellKey)) continue;
    if (def.defaultValue === undefined || def.defaultValue === null) continue;
    buckets.ARCANUM.push({
      name: def.name,
      displayName: def.displayName,
      value: def.defaultValue,
      formattedValue: formatValue(def.defaultValue, def.name),
      provenance: {
        inputName: def.name,
        value: def.defaultValue,
        source: 'default',
        confidence: 0.7,
        timestamp: '',
        defaultStatus: null,
      } as ProvenanceRecord,
      validRange: def.validRange,
    });
    arcanumUnreviewed++;
  }

  // Pass 2 — table cells missing from entries.
  for (const table of tableInputs ?? []) {
    for (const row of table.rows ?? []) {
      for (const column of table.columns ?? []) {
        const cellName = row.cells?.[column.key];
        if (typeof cellName !== 'string' || cellName === '') continue;
        if (seenNames.has(cellName)) continue;
        const cellValue = row.defaultValues?.[column.key];
        if (cellValue === undefined || cellValue === null) continue;
        const label =
          row.label && column.displayName
            ? `${row.label} – ${column.displayName}`
            : row.label || column.displayName || cellName;
        buckets.ARCANUM.push({
          name: cellName,
          displayName: label,
          value: cellValue,
          formattedValue: formatValue(cellValue, cellName),
          provenance: {
            inputName: cellName,
            value: cellValue,
            source: 'default',
            confidence: 0.7,
            timestamp: '',
            defaultStatus: null,
          } as ProvenanceRecord,
        });
        arcanumUnreviewed++;
      }
    }
  }

  // ADR 0026: fold resolved location inputs into the USER count and include
  // ALL schema location defs in the denominator so the CounterQuad aligns with
  // the server completeness-gate (ADR 0009/0003).
  //
  // Resolved predicate uses `isAddressedLocation` from `lib/version-completeness.ts`
  // (Phase 3 hardening, 2026-05-18): addressed = standard predicate (source !==
  // 'default' OR confirmed-default) AND dealMetadata[key] != null.
  // Fixes the previously-missing confirmed-default case where a location input
  // with defaultStatus:'confirmed' would still show "Property location — not set".
  // This is intentionally the SAME predicate the dedicated location section uses for
  // `isResolved` (single source of truth; no divergent predicate).
  let addressedLocationCount = 0;
  for (const def of locationInputDefs) {
    const provEntry = provenance[def.name];
    const entry: ProvenanceEntry | null =
      provEntry == null
        ? null
        : typeof provEntry === 'string'
        ? { source: provEntry }
        : (provEntry as ProvenanceEntry);
    const hasDealMeta = dealMetadata != null && dealMetadata[def.name] != null;
    if (isAddressedLocation(entry, hasDealMeta)) {
      addressedLocationCount++;
    }
  }

  // USER count = bucket rows (non-location, user-sourced) + resolved locations.
  const userCount = buckets.USER.length + addressedLocationCount;
  const arcanumCount = buckets.ARCANUM.length;
  const derivedCount = buckets.DERIVED.length;
  // Total = EDITABLE inputs only (USER + ARCANUM buckets) + ALL schema location
  // defs (resolved or not). This mirrors the server gate denominator:
  // schemaInputs.length (ADR 0009 counter-denominator rule, research §3 #27).
  // Using locationInputDefs.length ensures an unresolved location contributes
  // to the denominator even before it has a value in inputValues.
  //
  // builder-ux-overhaul FEATURE_END (2026-05-22): the read-only DERIVED bucket
  // (geocode Location.* sub-attrs — derivedFrom set, no InputDefinition, source
  // 'market_data') is EXCLUDED from the EDITABLE total. Those rows are not
  // addressable inputs and are absent from MODEL OVERVIEW's `totalInputs`
  // denominator; the previous `entries.filter(!locationInputNames.has(name))`
  // form counted them (it only excluded the EXACT top-level location name),
  // producing the 71/68 inversion the Inc-3 coherence work targets. Summing the
  // already-computed USER + ARCANUM bucket lengths is equivalent to the prior
  // non-location-entry count MINUS the DERIVED rows.
  // Counter-denominator coherence (new-development-input-roundtrip
  // 2026-05-26): align with MODEL OVERVIEW's `totalInputs` formula in
  // builder.tsx:1252-1258 (`scalarInputCount + tableCellCount`). Pre-fix
  // `total` was `USER + ARCANUM + locationInputDefs` which made the cold
  // EDITABLE counter read "1" (just the Location prompt slot) instead of
  // the full schema cardinality. Now both surfaces show the same denominator
  // — for multifamily-v1, EDITABLE always reads 68 (43 scalars + 25 table
  // cells). YOUR / ARCANUM / DERIVED still count what's currently in
  // inputProvenance, so progress is `YOUR / EDITABLE` against a stable 68.
  //
  // Falls back to the pre-fix formula when `tableInputs` is undefined
  // (back-compat for callers that don't thread the prop) so behavior is
  // unchanged in tests + share-mode renderers that omit it.
  const scalarInputCount = inputDefinitions?.length ?? 0;
  const tableCellCount =
    tableInputs?.reduce(
      (sum, t) => sum + t.rows.length * t.columns.length,
      0
    ) ?? 0;
  const total =
    tableInputs != null
      ? scalarInputCount + tableCellCount
      : buckets.USER.length + buckets.ARCANUM.length + locationInputDefs.length;

  // Empty-state gate (line below): "are there ANY displayable rows" — distinct
  // from the EDITABLE `total`. The DERIVED bucket is excluded from `total`
  // (read-only, not addressable), but a conversation with ONLY derived geocode
  // rows MUST still render the panel (its DERIVED section), not the empty
  // placeholder. Use the full displayable-row count for the gate.
  const hasDisplayableRows =
    buckets.USER.length +
      buckets.ARCANUM.length +
      buckets.DERIVED.length +
      locationInputDefs.length >
    0;

  return (
    <div className="flex flex-col border-t border-panel-border">
      {/* Header */}
      <div className="px-4 py-3 border-b border-panel-border flex flex-col gap-3">
        <div className="flex items-start justify-between gap-2">
          <span className="text-xs font-mono font-semibold text-text-muted tracking-[1.2px]">
            VARIABLES
          </span>
          <div className="flex items-center gap-1.5 flex-wrap justify-end">
            {/* AcceptedUnderwritingBadge — accepted-version STATUS indicator
                only. The Versions / Create version / Accept ACTIONS moved to
                the consolidated <ManageVersionsButton> on the tab row
                (conversation-model-versioning Phase 5). ADR-0019 is preserved
                by placement: this accent badge sits in the panel header while
                <CurrentVersionIndicator> (neutral) sits inline with the
                version list below — a user still cannot read "current" as
                "accepted". */}
            <AcceptedUnderwritingBadge
              acceptedUnderwritingVersionId={acceptedUnderwritingVersionId}
              conversationId={conversationId}
            />
          </div>
        </div>
        {/* CurrentVersionIndicator — INLINE with the version list / trigger area
            (ADR 0019: different screen placement from AcceptedUnderwritingBadge
            which is adjacent to the Accept control above). Always present once
            ≥1 version exists; neutral tokens (not accent). */}
        <CurrentVersionIndicator
          currentVersionId={currentVersionId}
          conversationId={conversationId}
        />
        <CounterQuad
          total={total}
          user={userCount}
          arcanum={arcanumCount}
          derived={derivedCount}
        />
        <SkippedMarketDataBadge
          skipped={skippedMarketData}
          fetchedVsApplied={fetchedVsApplied}
          appliedItems={appliedMarketFields}
        />
        {/* market-default-apply-ux Phase 2 Session 2.7 — teaser (slim).
            Standing per-row dropdown removed; renders a compact entry-point
            card that references the turn-anchored chat receipt (ADR 0003).
            Self-hides when readOnly or nothing to show. ADR 0006. */}
        <MarketDataApplyPanel
          rows={apply.rows}
          appliedMarketFields={appliedMarketFields}
          marketApplyCounts={marketApplyCounts}
          readOnly={readOnly}
        />
      </div>

      {/* Location inputs section — rendered outside the 3-tier bucket system.
          Sourced from inputDefinitions (schema-driven), NOT inputValues, so
          the affordance appears even when the location hasn't been set yet.
          ADR 0023: display + affordance only — NO inline <input>.
          The resolve+commit path goes through the shared <LocationInput>
          component rendered by <LocationInputModal> in routes/builder.tsx. */}
      {locationInputDefs.length > 0 && (
        <div data-testid="location-inputs-section">
          {locationInputDefs.map((def) => {
            // Read the resolved canonical address from inputValues (same source
            // the 3-tier view reads for all other inputs — research §2).
            const resolvedValue =
              typeof values[def.name] === 'string'
                ? (values[def.name] as string)
                : null;

            // An input is considered "set" (resolved) using `isAddressedLocation`
            // from `lib/version-completeness.ts` (Phase 3 hardening, 2026-05-18):
            //   addressed = standard predicate (source !== 'default' OR confirmed-default)
            //   AND dealMetadata[key] != null.
            // Fixes the confirmed-default edge-case where source:'default' +
            // defaultStatus:'confirmed' + resolution payload should render "Change"
            // not "Property location — not set". Display-only — server enforces gate.
            const provenanceEntry = provenance[def.name];
            const locationEntry: ProvenanceEntry | null =
              provenanceEntry == null
                ? null
                : typeof provenanceEntry === 'string'
                ? { source: provenanceEntry }
                : (provenanceEntry as ProvenanceEntry);
            const hasDealMetadata =
              dealMetadata != null && dealMetadata[def.name] != null;
            const isResolved =
              isAddressedLocation(locationEntry, hasDealMetadata) &&
              resolvedValue !== null;

            function handleAffordanceClick() {
              if (!conversationId) return;
              if (typeof window === 'undefined') return;
              try {
                window.dispatchEvent(
                  new CustomEvent('arcanum:open-location-input', {
                    detail: {
                      conversationId,
                      inputKey: def.name,
                      initialValue: resolvedValue ?? null,
                    },
                  })
                );
              } catch {
                // Defensive — CustomEvent unsupported envs shouldn't crash.
              }
            }

            return (
              <div
                key={def.name}
                className="px-4 py-3 border-b border-panel-border"
                data-testid="location-input-row"
                data-input-key={def.name}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <span className="text-xs font-mono text-text-muted tracking-[0.3px] truncate">
                      {def.displayName ?? def.name}
                    </span>
                    {isResolved ? (
                      <>
                        <span
                          className="text-sm font-mono text-text truncate"
                          data-testid="location-resolved-value"
                        >
                          {resolvedValue}
                        </span>
                        {/* Inline resolved-location detail block
                          (new-development-input-roundtrip 2026-05-26).
                          Surfaces the geocoder sub-attributes (city, state,
                          zip, coordinates, metro, floodZone, walkScore,
                          censusTract) under the resolved address so the user
                          sees what enrichment we picked up in-context.
                          Source: `dealMetadata[def.name]` (PropertyResolution
                          payload). Read-only — edits route through
                          set_property_address. These same sub-attrs are
                          filtered out of the DERIVED bucket above so they
                          don't render twice. */}
                        {(() => {
                          const detail = dealMetadata?.[def.name] as
                            | Partial<{
                                city: string | null;
                                state: string | null;
                                zip: string | null;
                                lat: number;
                                lng: number;
                                metro: string | null;
                                cbsa: string | null;
                                floodZone: string | null;
                                floodZoneLabel: string | null;
                                walkScore: number | null;
                                censusTract: string | null;
                              }>
                            | null
                            | undefined;
                          if (detail == null || typeof detail !== 'object') {
                            return null;
                          }
                          const cells: Array<{
                            label: string;
                            value: string;
                            wide?: boolean;
                          }> = [];
                          if (typeof detail.city === 'string' && detail.city) {
                            cells.push({ label: 'CITY', value: detail.city });
                          }
                          if (
                            typeof detail.state === 'string' &&
                            detail.state
                          ) {
                            cells.push({ label: 'STATE', value: detail.state });
                          }
                          if (typeof detail.zip === 'string' && detail.zip) {
                            cells.push({ label: 'ZIP', value: detail.zip });
                          }
                          if (
                            typeof detail.lat === 'number' &&
                            Number.isFinite(detail.lat) &&
                            typeof detail.lng === 'number' &&
                            Number.isFinite(detail.lng)
                          ) {
                            cells.push({
                              label: 'COORDS',
                              value: `${detail.lat.toFixed(
                                4
                              )}, ${detail.lng.toFixed(4)}`,
                            });
                          }
                          if (
                            typeof detail.metro === 'string' &&
                            detail.metro
                          ) {
                            cells.push({
                              label: 'METRO',
                              value: detail.metro,
                              wide: true,
                            });
                          }
                          if (
                            typeof detail.floodZone === 'string' &&
                            detail.floodZone
                          ) {
                            const label =
                              typeof detail.floodZoneLabel === 'string' &&
                              detail.floodZoneLabel
                                ? `${detail.floodZone} · ${detail.floodZoneLabel}`
                                : detail.floodZone;
                            cells.push({ label: 'FLOOD', value: label });
                          }
                          if (
                            typeof detail.walkScore === 'number' &&
                            Number.isFinite(detail.walkScore)
                          ) {
                            cells.push({
                              label: 'WALK',
                              value: String(detail.walkScore),
                            });
                          }
                          if (
                            typeof detail.censusTract === 'string' &&
                            detail.censusTract
                          ) {
                            cells.push({
                              label: 'CENSUS',
                              value: detail.censusTract,
                              wide: true,
                            });
                          }
                          if (cells.length === 0) return null;
                          return (
                            <div
                              data-testid="location-resolved-details"
                              className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-0.5"
                            >
                              {cells.map((c) => (
                                <div
                                  key={c.label}
                                  className={
                                    c.wide
                                      ? 'col-span-2 flex items-baseline gap-1.5 min-w-0'
                                      : 'flex items-baseline gap-1.5 min-w-0'
                                  }
                                >
                                  <span className="text-[8px] font-mono uppercase tracking-[1px] text-text-dim shrink-0">
                                    {c.label}
                                  </span>
                                  <span className="text-[10px] font-mono text-text-muted truncate">
                                    {c.value}
                                  </span>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </>
                    ) : (
                      <span
                        className="text-xs font-mono text-text-dim italic"
                        data-testid="location-unset-label"
                      >
                        Property location — not set
                      </span>
                    )}
                  </div>
                  <Button
                    variant="secondary"
                    disabled={!conversationId}
                    onClick={handleAffordanceClick}
                    data-testid="location-affordance-button"
                    className="shrink-0 px-3 py-1 text-[10px] tracking-[0.5px] uppercase"
                    title={
                      !conversationId
                        ? 'Open a deal to set the property location.'
                        : isResolved
                        ? 'Change the property location.'
                        : 'Set the property location.'
                    }
                  >
                    {isResolved ? 'Change' : 'Set property location'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!hasDisplayableRows ? (
        <div
          className="px-4 py-6 text-center"
          data-testid="variables-empty-state"
        >
          <div className="text-xs text-text-dim font-mono">
            No inputs set yet
          </div>
          <div className="text-[10px] text-text-dim font-mono mt-1">
            Start by describing your deal
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto" ref={panelRef}>
          <BucketSection
            bucket="USER"
            rows={buckets.USER}
            expanded={bucketExpanded.USER}
            onToggle={() => toggleBucket('USER')}
            inputDefMap={inputDefMap}
            readOnly={readOnly}
          />
          <BucketSection
            bucket="ARCANUM"
            rows={buckets.ARCANUM}
            expanded={bucketExpanded.ARCANUM}
            onToggle={() => toggleBucket('ARCANUM')}
            inputDefMap={inputDefMap}
            subCounts={{
              confirmed: arcanumConfirmed,
              unreviewed: arcanumUnreviewed,
            }}
            readOnly={readOnly}
          />
          {/* DERIVED bucket: read-only geocode Location.* sub-attributes.
              Collapsed by default (supplementary data, not primary inputs).
              Empty-state (model with no geocode) handled by BucketSection's
              `rows.length === 0 → return null` guard — no awkward empty header.
              builder-ux-overhaul WS2 Session 4, ADR 0005. */}
          <BucketSection
            bucket="DERIVED"
            rows={buckets.DERIVED}
            expanded={bucketExpanded.DERIVED}
            onToggle={() => toggleBucket('DERIVED')}
            inputDefMap={inputDefMap}
            readOnly
          />
        </div>
      )}

      {/* Phase 3 Session 5: scroll-to-input deep-link listener. */}
      <ScrollToInputListener
        buckets={buckets}
        bucketExpanded={bucketExpanded}
        setBucketExpanded={setBucketExpanded}
        panelRef={panelRef}
      />

      {/* Orphaned Inputs — preserved from Phase 2 (set by fork() when an
          input from the source schema doesn't carry over: name removed or
          dataType mismatch). spec § 3c. */}
      {orphanedCount > 0 && (
        <>
          <button
            type="button"
            onClick={() => setShowOrphaned((v) => !v)}
            className="w-full px-4 py-2.5 border-b border-panel-border flex items-center justify-between bg-transparent border-none cursor-pointer hover:bg-warn/5 transition-colors"
            data-testid="orphaned-inputs-toggle"
          >
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono text-warn font-semibold tracking-[0.3px]">
                Inputs no longer in this model version ({orphanedCount})
              </span>
            </div>
            <span className="text-[10px] text-warn/60 font-mono">
              {showOrphaned ? '▲' : '▼'}
            </span>
          </button>
          {showOrphaned &&
            orphanedEntries.map(([key, info]) => (
              <div
                key={key}
                className="px-4 py-2 border-b border-panel-border flex flex-col gap-0.5 opacity-70"
                data-testid={`orphaned-input-${key}`}
              >
                <div className="flex justify-between items-center gap-2">
                  <span className="text-xs text-text-dim font-mono truncate">
                    {key}
                  </span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-[10px] font-mono font-semibold tracking-[0.5px] px-1.5 py-px rounded-[2px] border text-warn/70 bg-warn-dim/30 border-warn/15 uppercase">
                      orphaned
                    </span>
                    <span className="text-[10px] font-mono text-text-dim tracking-[0.3px]">
                      from v{info.originalSchemaVersion}
                    </span>
                  </div>
                </div>
                <span className="text-sm text-text-muted font-mono">
                  {formatValue(info.value, key)}
                </span>
              </div>
            ))}
        </>
      )}
    </div>
  );
}
